/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  HostListener,
  HostBinding,
} from '@angular/core';
import {
  UserService,
  User,
  AccessListRole,
  ProductPlanTier,
  EntitlementSpecialStatusCode,
} from '../../services/user.service';
import {
  AccessListConfig,
  AccessListService,
} from '../../services/access-list.service';
import { Router } from '@angular/router';
import { AuthGuardService } from '../../services/auth-guard.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TosUpdateModalComponent } from '../modals/tos-update-modal.component';
import { ErrorService } from '../../services/error.service';

@Component({
  selector: 'nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.less'],
  standalone: false,
})
export class NavBarComponent implements OnInit, OnDestroy {
  public menuItems: MenuItem[];
  userAuthSub: Subscription;
  accessListDiscoveredSub: Subscription;
  userLogoutSub: Subscription;
  configUpdatedSub: Subscription;
  isExpanded = false;
  displayVersion = false;
  displayMobileHamburger = false;
  isOwner = false;

  constructor(
    private element: ElementRef,
    private userService: UserService,
    private accessListService: AccessListService,
    public router: Router,
    private authGuardService: AuthGuardService,
    private xdaStringsService: XdaStringsService,
    private modalService: NgbModal,
    private errorService: ErrorService,
  ) {
    if (this.userService.user) {
      this.isOwner =
        this.userService.user.accessListRole === AccessListRole.Owner;
    }

    this.setMenuItems();

    if (this.xdaStringsService.strings['VERSION']) {
      this.displayVersion = true;
    }
  }

  ngOnInit(): void {
    this.userAuthSub = this.userService.userAuthenticated.subscribe((user) =>
      this.onUserAuthenticated(user),
    );
    this.accessListDiscoveredSub = this.userService.discovered.subscribe(() =>
      this.discover(),
    );
    this.userLogoutSub = this.userService.userLoggedOff.subscribe(() =>
      this.userLoggedOff(),
    );
    this.configUpdatedSub = this.accessListService.configUpdated.subscribe(
      (config) => this.configUpdated(config),
    );

    // make sure advanced subscription pathways are hidden when page is loaded
    this.toggleNavItem('active-documents', false);

    // get workflow config in init to ensure that the active documents page is shown on pages that are not the workflow page.
    if (this.userService.isLoggedOn() && this.userService.user) {
      this.accessListService.getAccessConfigConfig(
        this.userService.user.accessListId,
        true,
      );
      // If user is logged on but not a subscription user, re-discover (aka onUserAuthenticated)
      if (!this.userService.user.isSubscriptionUser) {
        this.onUserAuthenticated(this.userService.user);
      }
    }

    if (this.menuItems.filter((x) => x.canActivate == true).length > 0) {
      this.displayMobileHamburger = true;
    }
  }

  collapse() {
    this.isExpanded = false;
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  async onUserAuthenticated(user: User) {
    const result = await this.accessListService.discover(user);
    // Dismiss any modal that may have been displayed from login
    if (result == 'eula-required') {
      this.modalService.dismissAll('');
      // Delegate to UserService instead of duplicating logic
      this.userService.displayEula(null);

    } else if (result == 'not-discovered') {
      this.modalService.dismissAll('');
    }
  }

  async discover() {
    const role = this.userService?.user?.accessListRole ?? null;

    if (this.userService?.user?.isSubscriptionUser) {
      this.navigateToWorkflows();
    }

    this.setMenuItems();

    if (this.menuItems.filter((x) => x.canActivate == true).length > 0) {
      this.displayMobileHamburger = true;
    } else {
      this.displayMobileHamburger = false;
    }

    if (role === AccessListRole.Owner || role === AccessListRole.Manager) {
      this.toggleNavItem('access-list', true);
    }
    if (role === AccessListRole.Owner) {
      this.toggleNavItem('manage-subscription', true);
    }

    this.isOwner = role === AccessListRole.Owner;

    // Dismiss any modal that may have been displayed from login
    this.modalService.dismissAll('navigating');

    if (this.userService?.user?.isSubscriptionExpired) {
      const resp = await this.userService.getEntitlement(); // determine if trial subscription expired
      this.userService.displaySubscriptionExpiredAlert(
        EntitlementSpecialStatusCode[this.errorService.specialStatusCode],
        resp?.entitledStatusCode === 4,
      );
    } else if (this.userService?.user?.isSubscriptionUser) {
      this.navigateToWorkflows();
    }
    // get workflow config after the access list is discovered to ensure that the active documents tab is shown after a user logs in
    this.accessListService.getAccessConfigConfig(
      this.userService?.user?.accessListId ?? '',
      true,
    );
  }

  displayTryAgainAlert() {
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = 'SDE_PLEASE_TRY_AGAIN2';
    modalRef.componentInstance.buttonText = 'SDE_CLOSE';

    modalRef.result.then(() => {
      this.userService.logOff();
    });
  }

  setMenuItems(): void {
    // active subscription menu
    this.menuItems = [
      {
        route: 'workflows',
        label: 'SDE_WORKFLOWS',
        canActivate: this.authGuardService.canActivateRoute('workflows'),
      },
      {
        route: 'active-documents',
        label: 'SDE_ACTIVE_DOCUMENTS',
        canActivate: this.authGuardService.canActivateRoute('active-documents'),
      },
      {
        route: 'access-list',
        label: 'SDE_USERS',
        canActivate: this.authGuardService.canActivateRoute('access-list'),
      },
      {
        route: 'manage-subscription',
        label: 'SDE_MANAGE_SUBSCRIPTION',
        canActivate: this.authGuardService.canActivateRoute(
          'manage-subscription',
        ),
      },
    ];

    // expired subscription menu
    if (
      this.userService.user &&
      (this.userService.user.accessListRole === AccessListRole.Owner ||
        this.userService.user.accessListRole === AccessListRole.Manager)
    ) {
      if (this.userService.user.isSubscriptionExpired) {
        this.menuItems = [
          {
            route: 'access-list',
            label: 'SDE_USERS',
            canActivate: this.authGuardService.canActivateRoute('access-list'),
          },
        ];
      }
    }
  }

  navigateToWorkflows() {
    this.userService.editAGAccountIfRequired();
    this.toggleNavItem('workflows', true);

    if (
      location.pathname !== '/my-account' &&
      location.pathname !== '/manage-subscription' &&
      location.pathname !== '/access-list' &&
      location.pathname !== '/active-documents'
    ) {
      this.router.navigate(['workflows']);
    }
  }

  toggleNavItem(route: string, canActivate: boolean) {
    const navItem = this.menuItems.find((item) => item.route === route);
    if (navItem) {
      navItem.canActivate = canActivate;
    }
  }

  userLoggedOff() {
    this.toggleNavItem('active-documents', false);
    this.toggleNavItem('access-list', false);
    this.toggleNavItem('workflows', false);
    this.toggleNavItem('manage-subscription', false);
    this.isOwner = false;

    if (this.menuItems.filter((x) => x.canActivate == true).length > 0) {
      this.displayMobileHamburger = true;
    } else {
      this.displayMobileHamburger = false;
    }
  }

  configUpdated(config: AccessListConfig) {
    if (config === undefined) return;
    const subConfig = config,
      isAdvancedPlan =
        (this.userService.user?.acquisition?.productPlanTier &&
          this.userService.user?.acquisition?.productPlanTier >=
            ProductPlanTier.Advanced) ||
        false,
      drmId = 'efc18a10-34fc-b78a-092a-189fde2c81a8'; // DRM Workflow ID

    // only show active documents tab if user is part of an advanced plan
    if (isAdvancedPlan) {
      // only show the active documents tab if there is a config and DRM is not hidden
      const displayActiveDocs = !subConfig?.hiddenWorkflows?.includes(drmId);
      this.toggleNavItem('active-documents', displayActiveDocs);
    } else {
      this.toggleNavItem('active-documents', false);
    }
  }

  ngOnDestroy() {
    this.userAuthSub.unsubscribe();
    this.userLogoutSub.unsubscribe();
    this.accessListDiscoveredSub.unsubscribe();
  }

  // Handle sticky nav bar on scroll
  @HostBinding('class.sticky') isSticky: boolean;
  @HostListener('window:scroll', [])
  onScroll(): void {
    // Make nav bar sticky, if brand header scrolled off screen
    const brandHeaderHeight = 64; // 4rem
    if (window.pageYOffset > brandHeaderHeight) {
      this.isSticky = true;
    } else {
      this.isSticky = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickGlobal(event: MouseEvent) {
    if (
      this.isExpanded && // If dropdown is expanded,
      !this.element.nativeElement
        .querySelector('.mn-navbar-container')
        .contains(event.target) && // and click event comes from outside the mobile nav element
      (event.target as HTMLElement).className != 'mn-open-btn' &&
      (event.target as HTMLElement).className != 'xgl-menu'
    ) // (and isn't the button or glyph to open the menu) collapse
    {
      this.collapse();
    }

    // collapse mobile menu if it is open and the target of the click is a menu item
    if (
      this.isExpanded &&
      (event.target as HTMLElement).classList.contains('mn-menu-item-link')
    ) {
      this.collapse();
    }
  }
}

export interface MenuItem {
  route: string;
  label: string;
  canActivate: boolean;
}
